import { Component, Show, createUniqueId } from "solid-js";
import type { CMSTextAndMediaCTABlock } from "~/types/cms";
import { RichTextRenderer } from "~/components/renderers/RichTextRenderer";
import { resolveCMSLink } from "~/api/cms/utils";
import { resolveMediaUrl } from "~/utils/media";
import styles from "./TextAndMediaCTABlock.module.css";
import Button, { type ButtonVariant } from "~/components/ui/Button/Button";
import { ArrowIcon } from "~/components/ui/icons";
import { Heading } from "~/components/typography";
import { isValidHexColor } from "~/utils/validation";

export interface TextAndMediaCTABlockProps extends CMSTextAndMediaCTABlock {}

/**
 * TextAndMediaCTABlock Component
 *
 * Inline block version of ContentBlocks collection.
 * Two-column layout with text content (overline, headline, rich text, CTA) and media image.
 *
 * Structure:
 * - Full-width wrapper (for edge-to-edge background)
 * - Inner container (centered, max-width 1512px)
 * - Card element (visual styling with background and rounded corners)
 *
 * Features:
 * - Two visual styles: contentCard (with background) and contentElement (transparent with shadow)
 * - Configurable image position (left/right)
 * - Three size formats: horizontal, square, portrait
 */
export const TextAndMediaCTABlock: Component<TextAndMediaCTABlockProps> = (props) => {
  // SSR-stable, instance-unique id base so aria-labelledby/aria-describedby don't
  // collide when a page renders more than one TextAndMediaCTABlock (the literal
  // ids previously duplicated, breaking the aria references).
  const uid = createUniqueId();

  // Determine button variant based on style and appearance
  const getButtonVariant = (): ButtonVariant => {
    const isNoBackground = props.link?.appearance === "nobackground";
    const isContentElement = props.style === "contentElement";

    if (isNoBackground) {
      return isContentElement ? "text" : "textOnDark";
    }
    return isContentElement ? "secondary" : "onAccent";
  };

  // contentCard renders on a coloured card: custom colour, else channel branding,
  // else the dark default. contentElement stays transparent (ADE-908).
  const cardBackground = () => {
    if (props.style === "contentElement") return undefined;
    if (isValidHexColor(props.backgroundCustom)) return props.backgroundCustom!;
    const channelColor = props.backgroundChannel?.branding?.primaryColor;
    if (isValidHexColor(channelColor)) return channelColor!;
    return "#2C2D2D";
  };

  return (
    <section
      class={styles.blockWrapper}
      style={{ "background-color": isValidHexColor(props.sectionBgColour) ? props.sectionBgColour! : undefined }}
    >
      <div class={styles.innerContainer}>
        <div
          class={styles.card}
          classList={{
            [styles[props.style]]: !!props.style,
            [styles[props.size]]: !!props.size,
            [styles[props.position]]: !!props.position,
          }}
          role="region"
          aria-labelledby={props.headline ? `${uid}-title` : undefined}
          aria-describedby={props.richText ? `${uid}-desc` : undefined}
          style={{ "background-color": cardBackground() }}
        >
          <Show
            when={props.style === "contentElement"}
            fallback={
              <div
                class={styles.container}
                classList={{
                  [styles[props.style]]: !!props.style,
                  [styles[props.size]]: !!props.size,
                  [styles.left]: props.position === "left",
                }}
              >
                <div
                  class={styles.columnLeft}
                  classList={{
                    [styles[props.style]]: !!props.style,
                    [styles[props.size]]: !!props.size,
                  }}
                >
                  <div class={styles.firstGroup}>
                    <Show when={props.overline}>
                      <span
                        class={styles.contentOverline}
                        classList={{ [styles[props.style]]: !!props.style }}
                        id={`${uid}-overline`}
                      >
                        {props.overline}
                      </span>
                    </Show>
                    <Show when={props.headline}>
                      <Heading
                        variant="contentHeadline"
                        as="h2"
                        classList={{
                          [styles[props.style]]: !!props.style,
                          [styles[props.size]]: !!props.size,
                        }}
                        id={`${uid}-title`}
                      >
                        {props.headline}
                      </Heading>
                    </Show>
                    <Show when={props.richText}>
                      <div
                        class={styles.text}
                        classList={{
                          [styles[props.style]]: !!props.style,
                          [styles[props.size]]: !!props.size,
                        }}
                        id={`${uid}-desc`}
                      >
                        <RichTextRenderer content={props.richText} variant="onAccent" />
                      </div>
                    </Show>
                  </div>
                  <Show when={props.link?.label}>
                    <div class={styles.secondGroup}>
                      <Button
                        href={resolveCMSLink(props.link!, "TextAndMediaCTABlock")}
                        variant={getButtonVariant()}
                        icon={
                          props.link!.appearance !== "nobackground" &&
                          props.showArrowIcon !== false ? (
                            <ArrowIcon direction="right" />
                          ) : undefined
                        }
                        target={props.link!.newTab ? "_blank" : undefined}
                        aria-label={
                          props.link!.newTab
                            ? `${props.link!.label} — öffnet sich in einem neuen Tab`
                            : props.link!.label
                        }
                      >
                        {props.link!.label}
                      </Button>
                    </div>
                  </Show>
                </div>
                <Show when={props.media?.url}>
                  <div class={styles.columnRight}>
                    <div
                      class={styles.imageWrapper}
                      classList={{
                        [styles[props.style]]: !!props.style,
                        [styles[props.size]]: !!props.size,
                        [styles.left]: props.position === "left",
                      }}
                    >
                      <img
                        src={resolveMediaUrl(props.media!.url) || ""}
                        alt={props.media!.alt || ""}
                        class={styles.image}
                        loading="lazy"
                      />
                    </div>
                  </div>
                </Show>
              </div>
            }
          >
            {/* contentElement: no image, overline+headline left, richText+button right */}
            <div
              class={styles.container}
              classList={{
                [styles.contentElement]: true,
                [styles.left]: props.position === "left",
              }}
            >
              <div class={styles.colHeadings}>
                <Show when={props.overline}>
                  <span
                    class={styles.contentOverline}
                    id={`${uid}-overline`}
                  >
                    {props.overline}
                  </span>
                </Show>
                <Show when={props.headline}>
                  <Heading
                    variant="contentHeadline"
                    as="h2"
                    classList={{ [styles.contentElement]: true }}
                    id={`${uid}-title`}
                  >
                    {props.headline}
                  </Heading>
                </Show>
              </div>
              <div class={styles.colBody}>
                <Show when={props.richText}>
                  <div
                    class={styles.text}
                    classList={{ [styles.contentElement]: true }}
                    id={`${uid}-desc`}
                  >
                    <RichTextRenderer content={props.richText} variant="default" />
                  </div>
                </Show>
                <Show when={props.link?.label}>
                  <div class={styles.secondGroup}>
                    <Button
                      href={resolveCMSLink(props.link!, "TextAndMediaCTABlock")}
                      variant={getButtonVariant()}
                      icon={
                        props.link!.appearance !== "nobackground" &&
                        props.showArrowIcon !== false ? (
                          <ArrowIcon direction="right" />
                        ) : undefined
                      }
                      target={props.link!.newTab ? "_blank" : undefined}
                      aria-label={
                        props.link!.newTab
                          ? `${props.link!.label} — öffnet sich in einem neuen Tab`
                          : props.link!.label
                      }
                    >
                      {props.link!.label}
                    </Button>
                  </div>
                </Show>
              </div>
            </div>
          </Show>
        </div>
      </div>
    </section>
  );
};

export default TextAndMediaCTABlock;
